import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Play, Package, Check, Star, User, Plus, Minus, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';
import { useApp } from '../context/AppContext';

import RecommendationList from '../components/RecommendationList';
import PageLoadingState from '../components/PageLoadingState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

function ImageModal({ isOpen, onClose, imageUrl, productName }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] md:max-w-[70vw] lg:max-w-[50vw] p-0 overflow-hidden bg-black/90 border-white/10">
        <DialogTitle className="sr-only">
          Enlarged view of {productName}
        </DialogTitle>
        <div className="relative w-full h-full flex items-center justify-center min-h-[50vh]">
          <img
            src={imageUrl}
            alt={productName}
            className="max-w-full max-h-[85vh] object-contain"
          />
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
            <p className="text-white text-center font-medium">{productName}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductDetail() {
  const { fetchCartCount } = useApp();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState("");
  const [note, setNote] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    productName: "",
  });

  const fetchProduct = useCallback(async () => {
    try {
      // Try to fetch by slug first
      const response = await axios.get(`${API}/products/slug/${slug}`);
      setProduct(response.data);
    } catch (error) {
      try {
        // Fallback to fetch by ID if slug fetch fails
        // This handles cases where the link might still be using the UUID
        const response = await axios.get(`${API}/products/${slug}`);
        setProduct(response.data);
      } catch (innerError) {
        toast.error('Product not found');
        navigate('/products');
      }
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  const fetchReviews = useCallback(async () => {
    try {
      // Try to fetch reviews by slug
      const response = await axios.get(`${API}/products/slug/${slug}/reviews`);
      setReviews(response.data);
    } catch (error) {
      try {
        // Fallback to fetch reviews by ID
        const response = await axios.get(`${API}/products/${slug}/reviews`);
        setReviews(response.data);
      } catch (innerError) {
        console.error('Failed to fetch reviews:', innerError);
      }
    }
  }, [slug]);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [fetchProduct, fetchReviews]);

  const handleAddToCart = async () => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    if (product.colors && product.colors.length > 0 && !selectedColor) {
      toast.error('Please select a colour');
      return;
    }

    setAddingToCart(true);
    try {
      console.log('Adding to cart:', { product_id: product.id, quantity: quantity, color: selectedColor, note: note });
      const response = await axios.post(
        `${API}/cart/add`,
        { product_id: product.id, quantity: quantity, color: selectedColor, note: note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Add to cart response:', response.data);
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 2000);
      toast.success('Added to cart!');
      // Update cart count from response if available, or fetch it
      fetchCartCount();
      setShowCheckout(true);
    } catch (error) {
      console.error('Failed to add to cart:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <PageLoadingState title="Loading product details" />
    );
  }

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-2 gap-12"
        >
          <div className="space-y-4">
            <div className="relative glass-morphic rounded-2xl overflow-hidden aspect-square group" data-testid="main-product-image">
              {product.images && product.images.length > 0 ? (
                <>
                  <img
                    src={getImageUrl(product.images[selectedImage])}
                    alt={product.name}
                    className="w-full h-full object-cover transition-all duration-300 cursor-zoom-in hover:scale-105"
                    onClick={() => setImageModal({
                      isOpen: true,
                      imageUrl: getImageUrl(product.images[selectedImage]),
                      productName: product.name,
                    })}
                  />
                  
                  {product.images.length > 1 && (
                    <>
                      <button
                        onClick={() => setSelectedImage((prev) => (prev === 0 ? product.images.length - 1 : prev - 1))}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-black/70"
                        data-testid="prev-image-button"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                      </button>
                      
                      <button
                        onClick={() => setSelectedImage((prev) => (prev === product.images.length - 1 ? 0 : prev + 1))}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-black/70"
                        data-testid="next-image-button"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                      </button>
                      
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2" data-testid="image-indicators">
                        {product.images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedImage(idx)}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                              selectedImage === idx 
                                ? 'bg-primary w-8' 
                                : 'bg-white/50 hover:bg-white/80'
                            }`}
                            data-testid={`indicator-${idx}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted/20">
                  <Package className="h-24 w-24 text-muted-foreground" />
                </div>
              )}
            </div>

            {product.images && product.images.length > 1 && (
              <div className="relative">
                <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide" data-testid="thumbnail-gallery">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`glass-morphic rounded-lg overflow-hidden aspect-square flex-shrink-0 w-20 h-20 transition-all duration-300 ${
                        selectedImage === idx 
                          ? 'ring-2 ring-primary scale-105' 
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      data-testid={`thumbnail-${idx}`}
                    >
                      <img src={getImageUrl(img)} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.youtube_url && (
              <div className="glass-morphic rounded-2xl p-6" data-testid="youtube-video">
                <div className="flex items-center gap-3 mb-4">
                  <Play className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold">Product Video</h3>
                </div>
                <div className="aspect-video rounded-lg overflow-hidden">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${product.youtube_url.split('v=')[1]?.split('&')[0] || product.youtube_url.split('/').pop()}`}
                    title="Product video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-2" data-testid="product-name">
                {product.name}
              </h1>
              
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-1 bg-yellow-500/10 text-yellow-500 px-3 py-1 rounded-full border border-yellow-500/20">
                  <Star className="h-4 w-4 fill-yellow-500" />
                  <span className="font-semibold">{product.average_rating?.toFixed(1) || '0.0'}</span>
                </div>
                <span className="text-sm text-muted-foreground">{product.review_count || 0} Reviews</span>
              </div>

              <div className="flex items-center gap-6 mb-4">
                <p className="text-3xl font-bold text-primary" data-testid="product-price">₹{product.price.toFixed(2)}</p>
                {product.tags && product.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {product.tags.map(tag => (
                      <span key={tag} className="text-xs uppercase tracking-wider bg-primary/10 text-primary px-3 py-1 rounded-full border border-primary/20">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="glass-morphic rounded-2xl p-6" data-testid="product-description">
              <h3 className="text-lg font-semibold mb-3">Description</h3>
              <p className="text-muted-foreground leading-relaxed">{product.description}</p>
            </div>

            <div className="glass-morphic rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-4">Features</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">High-quality 3D printed material</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">Precision engineered design</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">Durable and long-lasting</span>
                </li>
              </ul>
            </div>

            {product.colors && product.colors.length > 0 && (
              <div className="glass-morphic rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4">Available Colours</h3>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 rounded-full border transition-all duration-300 ${
                        selectedColor === color
                          ? 'bg-primary border-primary text-white shadow-[0_0_15px_rgba(139,92,246,0.3)]'
                          : 'border-white/10 hover:border-white/30 text-muted-foreground'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="glass-morphic rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Custom Note (Optional)</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="note" className="text-sm text-muted-foreground">
                  Any specific instructions or requirements?
                </Label>
                <Textarea
                  id="note"
                  placeholder="e.g. Please use a specific shade of blue, or add a name tag..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="bg-white/5 border-white/10 min-h-[100px] focus:ring-primary"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <span className="text-lg font-semibold">Quantity</span>
              <div className="flex items-center gap-2 bg-muted/20 rounded-full p-1 border border-white/10">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                  disabled={quantity <= 1}
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-8 text-center font-semibold text-lg">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <Button
                onClick={handleAddToCart}
                disabled={addingToCart || isSuccess}
                className={`w-full rounded-full py-6 text-lg shadow-lg transition-all duration-500 hover:-translate-y-1 ${
                  isSuccess 
                    ? 'bg-green-500 hover:bg-green-600 shadow-green-500/20' 
                    : 'bg-primary hover:bg-primary/90 shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)]'
                }`}
                data-testid="add-to-cart-button"
              >
                {addingToCart ? (
                  <>
                    <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Adding...
                  </>
                ) : isSuccess ? (
                  <>
                    <Check className="h-5 w-5 mr-2" />
                    Added!
                  </>
                ) : (
                  <>
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Add to Cart
                  </>
                )}
              </Button>

              {showCheckout && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="pt-2"
                >
                  <Button
                    onClick={() => navigate('/cart')}
                    variant="outline"
                    className="w-full rounded-full border-primary text-primary hover:bg-primary/10 py-6 text-lg transition-all duration-300"
                  >
                    Proceed to Checkout
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Reviews Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-20"
        >
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-semibold">Customer Reviews</h2>
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-5 w-5 ${
                      star <= Math.round(product.average_rating || 0)
                        ? 'fill-yellow-500 text-yellow-500'
                        : 'text-muted-foreground'
                    }`}
                  />
                ))}
              </div>
              <span className="font-semibold text-lg">{product.average_rating?.toFixed(1) || '0.0'} out of 5</span>
            </div>
          </div>

          {reviews.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-6">
              {reviews.map((review) => (
                <div key={review.id} className="glass-morphic rounded-2xl p-6 border border-white/5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                        <User className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-semibold">{review.user_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${
                            star <= review.rating ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed italic">"{review.comment}"</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 glass-morphic rounded-2xl border border-white/5 border-dashed">
              <p className="text-muted-foreground italic">No reviews yet. Be the first to review this product!</p>
            </div>
          )}
        </motion.div>

        {product && (
          <RecommendationList 
            productId={product.id} 
            title="You might also like" 
          />
        )}
      </div>

      <ImageModal
        isOpen={imageModal.isOpen}
        onClose={() => setImageModal({ ...imageModal, isOpen: false })}
        imageUrl={imageModal.imageUrl}
        productName={imageModal.productName}
      />
    </div>
  );
}
